// src/utils/exportStudentProgressToExcel.js

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const ZOOM_IMPORT_SHEET_NAME = 'Zoom Progress Import';

const getProgressNumber = (value, key = 'certificateProgress') => {
  if (value && typeof value === 'object') {
    const progress = Number(value.progress) || 0;
    const zoomprogress = Number(value.zoomprogress) || 0;
    return Number(value[key] ?? Math.max(progress, zoomprogress)) || 0;
  }

  return Number(value) || 0;
};

// Added studentQuizAnswersMap
export const exportStudentProgressToExcel = ({
  studentList, // List of studentofsubject enrollments for the current subject
  contentList,
  progressData,
  contentQuestionsMap, // For survey: Map<contentId, surveyQuestionsArray>; surveyQuestionsArray has [{name (id), title, type, rows?, columns?}, ...]
  // For quiz: Map<contentId, quizQuestionListArray>; quizQuestionListArray has [{question: "text"}, ...]
  studentProcessedQuizAnswersMap, // For quiz: Map<studentId, Map<contentId, arrayOfAnswerDetails>>; arrayOfAnswerDetails is [{useranswer,...}, ...]
  studentSurveyAnswersMap, // For survey: Map<studentId, Map<contentId, surveyAnswersObject>>; surveyAnswersObject is {"qId1": "ans", "qId2": ["ans"]}
  t,
}) => {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Student Progress (existing functionality - no changes here)
  const progressHeaders = [
    t('ManageEnrollSubjectPage.table_header_student_name', 'Student Name'),
    ...contentList.map((c) => c.name),
  ];
  const progressRows = studentList.map((studentItem) => {
    const studentId = studentItem.student?._id;
    const progressMap = progressData?.get(studentId) || new Map();
    const fullName =
      `${studentItem.student?.firstname || ''} ${studentItem.student?.lastname || ''}`.trim() ||
      t('ManageEnrollSubjectPage.name_not_available', 'Name not available');
    const row = [
      fullName,
      ...contentList.map((content) => {
        const val = progressMap.get(content._id);
        return val !== undefined ? `${getProgressNumber(val)}%` : '0%';
      }),
    ];
    return row;
  });
  const progressWorksheet = XLSX.utils.aoa_to_sheet([progressHeaders, ...progressRows]);
  XLSX.utils.book_append_sheet(
    workbook,
    progressWorksheet,
    t('ManageEnrollSubjectPage.student_progress_sheet_name', 'Student Progress')
  );

  const videoContents = (contentList || []).filter((content) => content.type === 'video');
  if (videoContents.length > 0) {
    const zoomHeaders = [
      'studentId',
      'studentName',
      'username',
      'contentId',
      'contentName',
      'currentProgress',
      'currentZoomProgress',
    ];

    const zoomRows = [];
    studentList.forEach((studentItem) => {
      const studentId = studentItem.student?._id;
      const fullName =
        `${studentItem.student?.firstname || ''} ${studentItem.student?.lastname || ''}`.trim() ||
        t('ManageEnrollSubjectPage.name_not_available', 'Name not available');
      const username = studentItem.student?.email || studentItem.student?.mobile || '';
      const progressMap = progressData?.get(studentId) || new Map();

      videoContents.forEach((content) => {
        const progressValue = progressMap.get(content._id);
        zoomRows.push([
          studentId || '',
          fullName,
          username,
          content._id,
          content.name,
          getProgressNumber(progressValue, 'progress'),
          getProgressNumber(progressValue, 'zoomprogress'),
        ]);
      });
    });

    const zoomWorksheet = XLSX.utils.aoa_to_sheet([zoomHeaders, ...zoomRows]);
    XLSX.utils.book_append_sheet(workbook, zoomWorksheet, ZOOM_IMPORT_SHEET_NAME);
  }

  // New Sheets: One per content item, showing student answers to questions from that content item.
  // studentAnswers is an array of studentofsubject objects.
  // Each object has a 'student' and an 'answers' array [{question_text, answer_text}, ...].
  // contentList is an array of content items [{_id, name}, ...].
  // contentQuestionsMap is Map<contentId, questionListFromConfig[]>; questionListFromConfig has {question: "question text", ...}
  // studentProcessedQuizAnswersMap is Map<studentId, Map<contentId, arrayOfAnswerDetails>>
  // where arrayOfAnswerDetails is [{useranswer: "...", answer: "...", status: "..."}, ...]

  // Iterate through each content item to create a sheet
  if (contentList && contentList.length > 0 && contentQuestionsMap) {
    contentList.forEach((contentItem) => {
      const contentId = contentItem._id;
      const contentName = contentItem.name || t('ManageEnrollSubjectPage.unknown_content', 'Unknown Content');
      const contentType = contentItem.type;

      const questionsFromConfig = contentQuestionsMap.get(contentId);

      if (!questionsFromConfig || questionsFromConfig.length === 0) {
        console.warn(`No questions configured for content: ${contentName} (ID: ${contentId}). Skipping sheet.`);
        return; // Skip this content item if it has no questions defined
      }

      // Initialize headers and a way to map Excel columns back to question/row IDs for answer lookup
      let dynamicHeaders = [];
      const headerInfoList = []; // Will store { questionId, rowId (for matrix), headerText }

      if (contentType === 'quiz') {
        questionsFromConfig.forEach((qDef, index) => {
          const headerText = qDef?.question || `${t('Unnamed Question')} ${index + 1}`;
          dynamicHeaders.push(headerText);
          headerInfoList.push({ questionIndex: index, headerText: headerText, type: 'quiz' });
        });
      } else if (contentType === 'survey') {
        questionsFromConfig.forEach((qDef) => {
          // qDef is from surveyconfig.surveyQuestions.questions
          const questionId = qDef?.name; // This is the UUID-like ID
          const questionTitle = qDef?.title || t('Unnamed Survey Question');

          if (qDef?.type === 'matrix') {
            qDef.rows?.forEach((row) => {
              // console.log('🚀 ~ questionsFromConfig.forEach ~ qDef:', row, qDef.columns);
              const headerText = `${questionTitle} - ${row.text || t('Unnamed Row')}`;
              dynamicHeaders.push(headerText);
              headerInfoList.push({
                questionId: questionId,
                rowId: row.value,
                headerText: headerText,
                type: 'survey_matrix_row',
                matrixColumns: qDef.columns,
              });
            });
          } else {
            // Simple survey question types (text, radiogroup, checkbox, etc.)
            dynamicHeaders.push(questionTitle);
            headerInfoList.push({ questionId: questionId, headerText: questionTitle, type: 'survey_simple' });
          }
        });
      } else {
        // For other content types, if they have questions in questionsFromConfig but aren't quiz/survey
        // Fallback or skip, for now, let's assume they might follow quiz structure if questionsFromConfig is populated.
        // This part might need more specific handling if other types with questions arise.
        questionsFromConfig.forEach((q, index) => {
          const headerText = q?.question || `${t('Unnamed Question')} ${index + 1}`;
          dynamicHeaders.push(headerText);
          headerInfoList.push({ questionIndex: index, headerText: headerText, type: 'unknown_config_type' });
        });
      }

      const sheetHeaders = [t('ManageEnrollSubjectPage.table_header_student_name', 'Student Name'), ...dynamicHeaders];

      const sheetRows = studentList.map((studentEnrollment) => {
        const student = studentEnrollment.student;
        const studentId = student?._id;
        const studentName = student
          ? `${student.firstname || ''} ${student.lastname || ''}`.trim()
          : t('ManageEnrollSubjectPage.name_not_available', 'Name not available');

        const rowData = [studentName];

        headerInfoList.forEach((hInfo) => {
          // console.log('🚀 ~ headerInfoList.forEach ~ hInfo:', hInfo.type);
          let cellAnswer = '';
          if (hInfo.type === 'quiz' && studentId && studentProcessedQuizAnswersMap) {
            const processedAnswersForStudentMap = studentProcessedQuizAnswersMap.get(studentId);
            const arrayOfAnswerDetails = processedAnswersForStudentMap?.get(contentId);
            if (arrayOfAnswerDetails && arrayOfAnswerDetails[hInfo.questionIndex]) {
              cellAnswer = arrayOfAnswerDetails[hInfo.questionIndex]?.useranswer || '';
            }
          } else if (hInfo.type === 'survey_simple' && studentId && studentSurveyAnswersMap) {
            const surveyAnswersForStudentMap = studentSurveyAnswersMap.get(studentId);
            const surveyAnswersObject = surveyAnswersForStudentMap?.get(contentId);
            if (surveyAnswersObject && surveyAnswersObject[hInfo.questionId] !== undefined) {
              cellAnswer = surveyAnswersObject[hInfo.questionId];
            }
          } else if (hInfo.type === 'survey_matrix_row' && studentId && studentSurveyAnswersMap) {
            const surveyAnswersForStudentMap = studentSurveyAnswersMap.get(studentId);
            const surveyAnswersObject = surveyAnswersForStudentMap?.get(contentId); // This is {"qId": {"rowId": colValue}}
            const matrixAnswerData = surveyAnswersObject ? surveyAnswersObject[hInfo.questionId] : null;

            if (matrixAnswerData && matrixAnswerData[hInfo.rowId] !== undefined) {
              const columnValue = matrixAnswerData[hInfo.rowId];
              // console.log('🚀 ~ headerInfoList.forEach ~ columnValue:', columnValue);
              // Attempt to map columnValue to column.text for readability
              const columnDefinition = hInfo.matrixColumns?.find((col) => col.value === columnValue);
              // console.log('🚀 ~ headerInfoList.forEach ~ columnDefinition:', columnDefinition);
              cellAnswer = columnDefinition?.text || String(columnValue); // Fallback to raw value
            }
          }
          // Common answer formatting
          if (Array.isArray(cellAnswer)) {
            cellAnswer = cellAnswer.join(', ');
          }
          rowData.push(String(cellAnswer));
        });
        return rowData;
      });

      const answerWorksheet = XLSX.utils.aoa_to_sheet([sheetHeaders, ...sheetRows]);
      // Sanitize sheet name
      const sanitizedSheetName = contentName.substring(0, 31).replace(/[*?:/\\[\]]/g, '_');
      XLSX.utils.book_append_sheet(workbook, answerWorksheet, sanitizedSheetName);
    });
  }

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(blob, 'student_data.xlsx'); // Changed filename to reflect more general data
};
